import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByFn, InputObservable } from '@y42/angular/util';
import { JobStatus } from '@y42/json-schema';
import { UnwrapArray } from '@y42/typescript';
import { map, Observable } from 'rxjs';
import { JobStatusToIcon, noJobIcon, StatusName } from '../../state';

type JobStatusList = Array<{ type: JobStatus; count: number }>;
type JobStatusListable = JobStatus | JobStatusList;

@UntilDestroy()
@Component({
  selector: 'y42-job-status',
  templateUrl: './job-status.component.html',
  styleUrls: ['./job-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobStatusComponent {
  @Input()
  @InputObservable()
  jobStatus?: JobStatusListable;
  jobStatus$!: Observable<JobStatusListable>;

  protected readonly jobStatusSingle$ = this.jobStatus$.pipe(
    map((value): JobStatus | undefined => (!Array.isArray(value) ? value : undefined)),
  );
  private readonly jobStatusList$ = this.jobStatus$.pipe(
    map((value): JobStatusList | undefined => (Array.isArray(value) ? value : undefined)),
  );

  readonly defaultStatusData = {
    label: StatusName.NoJob,
    icon: noJobIcon,
  };
  readonly JobStatus = JobStatus;
  readonly JobStatusToIcon = JobStatusToIcon;
  readonly StatusName = StatusName;

  readonly trackByType = getTrackByFn<UnwrapArray<JobStatusList>>('type');

  readonly state = connectState(this, {
    valueAsSingle: this.jobStatusSingle$.pipe(map((value) => (value ? this.valueMap(value) : undefined))),
    valueAsArray: this.jobStatusList$.pipe(
      map((values) =>
        values?.map((value) => {
          return {
            ...this.valueMap(value.type),
            count: value.count,
          };
        }),
      ),
    ),
  });

  private valueMap(value: JobStatus) {
    if (!JobStatusToIcon[value]) {
      return this.defaultStatusData;
    }

    const { icon, ...props } = JobStatusToIcon[value];
    return {
      ...props,
      icon: icon,
    };
  }
}
